import { SignerFieldsWarning } from "./SignerFieldsWarning";
import { cn } from "@/lib/utils/cn";
import { SIGNER_COLORS } from "../SignatureRequestPdfLayoutEditor.constants";
import {
  Button,
  Form,
  PhoneNumberFormField,
  TextFormField,
  Typography,
} from "@/components/ui";
import { Avatar, AvatarFallback } from "@/components/ui/shadcn/Avatar/Avatar";
import {
  formatContactsName,
  formatLocalOrIntlPhoneNumber,
  formatUsersName,
} from "@/lib/utils/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { Mail, Pencil, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  getSignatureRequestSignerSlotIndex,
  isContactSigner,
  type SignatureRequestSigner,
} from "../signatureRequestSigners";
import {
  signerEditFormSchema,
  type SignerEditFormValues,
} from "./signerEditSchema";

type ReviewRecipientCardProps = {
  index: number;
  colorIndex?: number;
  hasFields: boolean;
  signer: SignatureRequestSigner;
  signers: SignatureRequestSigner[];
  setSigners: React.Dispatch<React.SetStateAction<SignatureRequestSigner[]>>;
};

function getInitials(name: string) {
  return name
    .split(" ")
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

export function ReviewRecipientCard({
  index,
  colorIndex,
  hasFields,
  signer,
  signers,
  setSigners,
}: ReviewRecipientCardProps) {
  const color = SIGNER_COLORS[(colorIndex ?? index) % SIGNER_COLORS.length];
  const [isEditing, setIsEditing] = useState(false);
  const contactSigner = isContactSigner(signer);
  const name =
    (contactSigner
      ? formatContactsName(signer.contact)
      : formatUsersName(signer.user)) ?? "Unnamed recipient";
  const email = contactSigner
    ? (signer.contact.emails[0]?.address ?? "")
    : signer.user.email;
  const phone = contactSigner
    ? (signer.contact.phones[0]?.number ?? "")
    : signer.user.phoneNumber;
  const slotIndex = getSignatureRequestSignerSlotIndex(signers, index) + 1;
  const form = useForm<SignerEditFormValues>({
    resolver: zodResolver(signerEditFormSchema),
    defaultValues: { email, phone },
  });

  useEffect(() => {
    if (isEditing) {
      form.reset({ email, phone });
    }
  }, [email, form, isEditing, phone]);

  const save = (values: SignerEditFormValues) => {
    const nextEmail = values.email.trim();
    const nextPhone = values.phone.trim();

    setSigners((previous) =>
      previous.map((currentSigner, currentIndex) => {
        if (currentIndex !== index) return currentSigner;

        if (isContactSigner(currentSigner)) {
          return {
            ...currentSigner,
            contact: {
              ...currentSigner.contact,
              emails: nextEmail
                ? currentSigner.contact.emails.length > 0
                  ? currentSigner.contact.emails.map((entry, emailIndex) =>
                      emailIndex === 0
                        ? { ...entry, address: nextEmail }
                        : entry,
                    )
                  : [{ ...currentSigner.contact.emails[0], address: nextEmail }]
                : [],
              phones: nextPhone
                ? currentSigner.contact.phones.length > 0
                  ? currentSigner.contact.phones.map((entry, phoneIndex) =>
                      phoneIndex === 0
                        ? { ...entry, number: nextPhone }
                        : entry,
                    )
                  : [{ ...currentSigner.contact.phones[0], number: nextPhone }]
                : [],
            },
          };
        }

        return {
          ...currentSigner,
          user: {
            ...currentSigner.user,
            email: nextEmail,
            phoneNumber: nextPhone,
          },
        };
      }),
    );
    setIsEditing(false);
  };

  return (
    <div className="border-border rounded-xl border p-3">
      <div className="flex items-start gap-3">
        <Avatar className="h-9 w-9">
          <AvatarFallback className={cn("text-xs font-semibold", color.pill)}>
            {getInitials(name)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-1.5">
              <Typography
                className={cn("truncate text-sm font-semibold", color.text)}
              >
                {name}
              </Typography>
              {!hasFields ? <SignerFieldsWarning kind={signer.kind} /> : null}
            </div>
            <Typography className="text-muted-foreground shrink-0 text-xs">
              {contactSigner
                ? `Signer ${slotIndex}`
                : `Firm signer ${slotIndex}`}
            </Typography>
            {!isEditing ? (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="text-muted-foreground h-7 w-7 shrink-0"
                aria-label={`Edit delivery details for ${name}`}
                onClick={() => {
                  setIsEditing(true);
                }}
              >
                <Pencil className="h-3.5 w-3.5" />
              </Button>
            ) : null}
          </div>

          {isEditing ? (
            <Form {...form}>
              <form
                className="mt-3 space-y-3"
                onSubmit={(event) => {
                  void form.handleSubmit(save)(event);
                }}
              >
                <TextFormField
                  label="Email"
                  placeholder="Enter email address"
                  register={form.register("email")}
                />
                <PhoneNumberFormField
                  label="Phone number"
                  register={form.register("phone")}
                />
                <div className="flex gap-2">
                  <Button type="submit" size="sm">
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setIsEditing(false);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </Form>
          ) : (
            <>
              <div className="text-muted-foreground mt-2 space-y-1.5 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <Mail className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{email || "No email"}</span>
                </div>
                <div className="flex min-w-0 items-center gap-2">
                  <Phone className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">
                    {phone
                      ? formatLocalOrIntlPhoneNumber({ number: phone }) || phone
                      : "No phone number"}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
